import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import {
  ClientEmailMatch,
  useCheckClientEmailsMutation,
} from "../../../slices/ContactsSlice";
import {
  duplicateEmailSentence,
  emailsToCheck,
  matchOwner,
} from "./duplicateEmail.utils";

/**
 * "This email is already used by … Save anyway?" (Roadmap 1784, owner
 * 2026-10-21: warn, never block). Used by the New individual / New
 * organization windows and the client Edit windows.
 *
 * `confirmEmails(emails, { before, exceptCustomerId })` resolves true when
 * saving may go on (no other client of the firm uses them, the check failed,
 * or the person clicked "Save anyway") and false for "Change email".
 */
export const useDuplicateEmailWarning = () => {
  const [checkEmails] = useCheckClientEmailsMutation();
  const [matches, setMatches] = useState<ClientEmailMatch[] | null>(null);
  const resolver = useRef<((proceed: boolean) => void) | null>(null);

  const answer = useCallback((proceed: boolean) => {
    resolver.current?.(proceed);
    resolver.current = null;
    setMatches(null);
  }, []);

  // A window closed while the question is open counts as "Change email".
  useEffect(() => () => resolver.current?.(false), []);

  const confirmEmails = useCallback(
    async (
      emails: (string | null | undefined)[],
      options: {
        before?: (string | null | undefined)[];
        exceptCustomerId?: string | null;
      } = {},
    ): Promise<boolean> => {
      const orgId = getLinkedFirm()?.orgId;
      const toCheck = emailsToCheck(emails, options.before);
      if (!orgId || toCheck.length === 0) return true;
      let found: ClientEmailMatch[] = [];
      try {
        const result = await checkEmails({
          orgId,
          emails: toCheck,
          exceptCustomerId: options.exceptCustomerId || null,
        }).unwrap();
        found = result?.matches ?? [];
      } catch {
        return true; // Only a warning: a failed check never stops saving.
      }
      if (found.length === 0) return true;
      return new Promise<boolean>((resolve) => {
        resolver.current = resolve;
        setMatches(found);
      });
    },
    [checkEmails],
  );

  const dialog: ReactNode = matches ? (
    <DuplicateEmailDialog
      matches={matches}
      onSaveAnyway={() => answer(true)}
      onChangeEmail={() => answer(false)}
    />
  ) : null;

  return { confirmEmails, dialog };
};

export const DuplicateEmailDialog: FC<{
  matches: ClientEmailMatch[];
  onSaveAnyway: () => void;
  onChangeEmail: () => void;
}> = ({ matches, onSaveAnyway, onChangeEmail }) => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onChangeEmail();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onChangeEmail]);

  return createPortal(
    <div
      className={
        "fixed inset-0 z-[1300] flex items-center justify-center bg-[rgba(24,26,38,0.5)] p-6"
      }
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onChangeEmail();
      }}>
      <div
        role={"alertdialog"}
        aria-modal={"true"}
        aria-label={"Email already used"}
        className={
          "w-full max-w-[440px] rounded-2xl bg-white px-[26px] pb-[22px] pt-[24px] shadow-[0_24px_60px_rgba(20,22,34,0.3)]"
        }>
        <div
          className={
            "mb-3 flex h-[44px] w-[44px] items-center justify-center rounded-full bg-[#FFF4E5] text-[22px] font-extrabold text-[#B45309]"
          }>
          !
        </div>
        <div className={"mb-1.5 text-[17px] font-extrabold text-[#1F2433]"}>
          Email already used
        </div>
        <p className={"text-[14px] leading-relaxed text-[#4B5263]"}>
          {duplicateEmailSentence(matches)} Save anyway?
        </p>
        <ul
          className={
            "mt-3 max-h-[160px] overflow-y-auto rounded-xl border border-[#EDEFF3] bg-[#F8F9FB] px-3.5 py-1"
          }>
          {matches.map((match) => (
            <li
              key={`${match.customerId}-${match.email}-${
                match.contactPersonName || ""
              }`}
              className={
                "border-b border-[#ECEFF3] py-2 text-[13px] last:border-b-0"
              }>
              <div className={"font-bold text-[#1F2433]"}>
                {matchOwner(match)}
              </div>
              <div className={"text-[#6B7185]"}>
                {match.type === "organization" ? "Organization" : "Individual"}{" "}
                · {match.email}
              </div>
            </li>
          ))}
        </ul>
        <div className={"mt-5 flex justify-end gap-2.5"}>
          <button
            type={"button"}
            autoFocus
            onClick={onChangeEmail}
            className={
              "h-10 rounded-[10px] border border-[#DCE2EA] bg-white px-4 text-[14px] font-bold text-[#1F2433] hover:bg-[#F6F7F9]"
            }>
            Change email
          </button>
          <button
            type={"button"}
            onClick={onSaveAnyway}
            className={
              "h-10 rounded-[10px] bg-[#6B4EFF] px-4 text-[14px] font-bold text-white hover:bg-[#5A3FE6]"
            }>
            Save anyway
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
